import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useBookData } from '../state/AppData';
import { resolveCity } from '../state/scope';

const BASE = 'https://1884.es/';
const MARCA = 'Castilla y León 1884';
const CLAIM = `${MARCA} · Leer el territorio, recorrer el libro`;
const RESUMEN = 'Recurso educativo abierto: el libro que José María Quadrado escribió en 1884 sobre Salamanca, Ávila y Segovia, leído sobre el mapa de hoy y cruzado con los datos abiertos de la Junta de Castilla y León.';

/** Título, descripción y dirección canónica de cada sección. Con direcciones reales (ADR-0016) esto
 *  ya no es solo lo que se lee en la pestaña: es lo que indexa un buscador y lo que se enseña cuando
 *  alguien comparte un pasaje. Se deriva de la ruta, del ámbito activo y del capítulo que se lee. */
export function Seo() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { index, cities } = useBookData();
  const city = resolveCity(cities, params.get('ciudad'));
  const capitulo = pathname.match(/^\/leer\/([a-z]{3}-c\d{2})/)?.[1];
  const cap = capitulo ? index.chapters.find((c) => c.chapterId === capitulo) : undefined;

  let titulo = CLAIM;
  let descripcion = RESUMEN;
  const en = city.inline; // «la Segovia», «la provincia de Ávila»

  if (cap) {
    titulo = `${cap.title} · ${cap.city ?? cap.province} en 1884`;
    descripcion = `«${cap.title}», capítulo ${cap.number} de ${cap.city ?? `la provincia de ${cap.province}`} en la obra de Quadrado (1884), páginas ${cap.printedPageStart}–${cap.printedPageEnd}, leído sobre el mapa de hoy.`;
  } else if (pathname.startsWith('/explorar')) {
    titulo = `Mapa de ${en} en 1884 · ${MARCA}`;
    descripcion = `Los lugares que Quadrado describe en ${en}, sobre el mapa actual, con su estado respecto a 1884 y la fuente que lo sostiene.`;
  } else if (pathname.startsWith('/itinerarios')) {
    titulo = `Itinerarios por ${en} en 1884 · ${MARCA}`;
    descripcion = `Recorridos guiados por ${en} siguiendo el orden del libro de 1884, con sus pasajes y sus lugares sobre el mapa.`;
  } else if (pathname.startsWith('/retos')) {
    titulo = `Retos de investigación · ${MARCA}`;
    descripcion = 'Actividades para identificar lugares por su descripción, reconstruir itinerarios y comprobar con fuentes qué se conserva de 1884.';
  } else if (pathname.startsWith('/aula')) {
    titulo = `Propuesta didáctica para el aula · ${MARCA}`;
    descripcion = 'Guía docente, secuencias temporizadas con ficha de alumnado y rúbricas para trabajar patrimonio, historia y datos abiertos en Secundaria y Bachillerato.';
  } else if (pathname.startsWith('/volumen')) {
    titulo = `El volumen de 1884, hoja a hoja · ${MARCA}`;
    descripcion = 'Facsímil completo del ejemplar de «Salamanca, Ávila y Segovia» (Quadrado, 1884), con su transcripción enfrentada.';
  } else if (pathname.startsWith('/buscar')) {
    titulo = `Buscar en el texto de 1884 · ${MARCA}`;
    descripcion = `Búsqueda sobre el texto íntegro de ${en} en la obra de Quadrado, con el pasaje y la página originales.`;
  } else if (pathname.startsWith('/proyecto/fuentes')) {
    titulo = `Fuentes, método y créditos · ${MARCA}`;
    descripcion = 'De dónde sale cada dato: el libro de 1884, la «Relación de monumentos» de la Junta de Castilla y León, OpenStreetMap, el IGN y Wikidata, con su licencia y su grado de certeza.';
  } else if (pathname.startsWith('/proyecto/aviso-legal')) {
    titulo = `Aviso legal y privacidad · ${MARCA}`;
    descripcion = 'Licencias, atribución y privacidad: sin cuentas, sin cookies, sin analítica y sin geolocalización.';
  } else if (pathname.startsWith('/ciudad')) {
    titulo = `${city.title} · ${MARCA}`;
    descripcion = `Qué escribió Quadrado sobre ${en} en 1884 y qué queda hoy, sobre el mapa actual y con los datos abiertos de la Junta.`;
  }

  useEffect(() => {
    document.title = titulo;
    const meta = (selector: string, valor: string) => {
      const el = document.head.querySelector<HTMLMetaElement>(selector);
      if (el) el.content = valor;
    };
    meta('meta[name="description"]', descripcion);
    meta('meta[property="og:title"]', titulo);
    meta('meta[property="og:description"]', descripcion);
    meta('meta[name="twitter:title"]', titulo);
    meta('meta[name="twitter:description"]', descripcion);
    // la dirección canónica no lleva los parámetros de selección (lugar, mención, párrafo): son
    // estados de la lectura, no documentos distintos. El ámbito sí, porque cambia el contenido.
    const ciudad = params.get('ciudad');
    const canonica = BASE + pathname.replace(/^\//, '') + (ciudad && pathname !== '/' ? `?ciudad=${ciudad}` : '');
    meta('meta[property="og:url"]', canonica);
    const link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (link) link.href = canonica;
  }, [titulo, descripcion, pathname, params]);

  return null;
}
